<template>
  <div class="reminder-cond-summary" :class="rootClass">
    <template v-if="condition">
      <div class="reminder-cond-summary__sec">
        <span class="reminder-cond-summary__h">予約するページ</span>
        <span v-if="hasSet" class="reminder-cond-summary__op">{{ setOperatorLabel }}</span>
        <ul v-if="hasSet" class="reminder-cond-summary__list">
          <li v-for="(node, i) in setNodes" :key="'set-' + i">
            <span class="reminder-cond-summary__m">{{ methodLabel(node.method) }}</span>
            {{ node.value }}
            <span v-if="node.duration" class="reminder-cond-summary__meta">滞在{{ node.duration }}秒</span>
            <span v-if="node.scroll" class="reminder-cond-summary__meta">スクロール{{ node.scroll }}%</span>
            <span v-if="hasQuery(node)" class="reminder-cond-summary__meta">クエリ{{ queryLabel(node) }}</span>
          </li>
        </ul>
        <div v-else-if="isManualEmptySet" class="reminder-cond-summary__none">任意のタイミングでスクリプトから予約</div>
        <div v-else class="reminder-cond-summary__none">(なし)</div>
      </div>
      <div class="reminder-cond-summary__sec">
        <span class="reminder-cond-summary__h">解除するページ</span>
        <span v-if="hasUnset" class="reminder-cond-summary__op">{{ unsetOperatorLabel }}</span>
        <ul v-if="hasUnset" class="reminder-cond-summary__list">
          <li v-for="(node, i) in unsetNodes" :key="'unset-' + i">
            <span class="reminder-cond-summary__m">{{ methodLabel(node.method) }}</span>
            {{ node.value }}
            <span v-if="node.duration" class="reminder-cond-summary__meta">滞在{{ node.duration }}秒</span>
            <span v-if="node.scroll" class="reminder-cond-summary__meta">スクロール{{ node.scroll }}%</span>
            <span v-if="hasQuery(node)" class="reminder-cond-summary__meta">クエリ{{ queryLabel(node) }}</span>
          </li>
        </ul>
        <div v-else class="reminder-cond-summary__none">(なし)</div>
      </div>
      <div class="reminder-cond-summary__sec">
        <span class="reminder-cond-summary__h">除外するページ</span>
        <span v-if="hasExclude" class="reminder-cond-summary__op">{{ excludeOperatorLabel }}</span>
        <ul v-if="hasExclude" class="reminder-cond-summary__list">
          <li v-for="(node, i) in excludeNodes" :key="'exclude-' + i">
            <span class="reminder-cond-summary__m">{{ methodLabel(node.method) }}</span>
            {{ node.value }}
            <span v-if="node.duration" class="reminder-cond-summary__meta">滞在{{ node.duration }}秒</span>
            <span v-if="node.scroll" class="reminder-cond-summary__meta">スクロール{{ node.scroll }}%</span>
            <span v-if="hasQuery(node)" class="reminder-cond-summary__meta">クエリ{{ queryLabel(node) }}</span>
          </li>
        </ul>
        <div v-else class="reminder-cond-summary__none">(なし)</div>
      </div>
      <div v-if="showTrigger && triggerLabel" class="reminder-cond-summary__trigger">トリガー: {{ triggerLabel }}</div>
    </template>
    <div v-else class="reminder-cond-summary__empty">配信条件未設定</div>
  </div>
</template>

<script>
// リマインダー配信条件(v1 RuleRoot)の読み取り専用サマリ表示。
// 確認画面・メッセージ一覧・成果一覧・成果詳細カード で共通利用するため、
// condition プロパティ 1 つで `ReminderMessageCondition` モデルそのまま(trigger_type / condition_set / condition_unset / condition_exclude)を受け取れるようにしている。
// v1 本リリースでは PagesGroup 配下のネストグループは禁止だが、既存 JSON に万一ネストが混在していても平坦化して描画する(防御的)。
export default {
  name: 'ReminderConditionSummary',
  props: {
    condition: { type: Object, default: null },
    mode: { type: String, default: 'detail' },
    showTrigger: { type: Boolean, default: false },
  },
  computed: {
    rootClass: function () {
      return 'reminder-cond-summary--' + (this.mode || 'detail');
    },
    setRoot: function () {
      return this.condition ? this.condition.condition_set : null;
    },
    unsetRoot: function () {
      return this.condition ? this.condition.condition_unset : null;
    },
    excludeRoot: function () {
      return this.condition ? this.condition.condition_exclude : null;
    },
    setNodes: function () {
      return this._extractUrlNodes(this.setRoot);
    },
    unsetNodes: function () {
      return this._extractUrlNodes(this.unsetRoot);
    },
    excludeNodes: function () {
      return this._extractUrlNodes(this.excludeRoot);
    },
    hasSet: function () {
      return this.setNodes.length > 0;
    },
    hasUnset: function () {
      return this.unsetNodes.length > 0;
    },
    hasExclude: function () {
      return this.excludeNodes.length > 0;
    },
    hasAnyCondition: function () {
      return this.hasSet || this.hasUnset || this.hasExclude;
    },
    // trigger=manual は「予約するページ」を CMS 側で持たない運用のため、
    // set 空 = 「任意のタイミングで発火」であることを明示的に案内する。
    isManualEmptySet: function () {
      return !!(this.condition && this.condition.trigger_type === 'manual' && !this.hasSet);
    },
    setOperatorLabel: function () {
      return this._operatorLabel(this._operatorOf(this.setRoot));
    },
    unsetOperatorLabel: function () {
      return this._operatorLabel(this._operatorOf(this.unsetRoot));
    },
    excludeOperatorLabel: function () {
      return this._operatorLabel(this._operatorOf(this.excludeRoot));
    },
    triggerLabel: function () {
      var t = this.condition && this.condition.trigger_type;
      if (t === 'auto') return '自動';
      if (t === 'manual') return '手動';
      return '';
    },
  },
  methods: {
    // RuleRoot → PagesGroup.children の UrlNode を走査して配列化。
    // v1 本リリース要件では PagesGroup 配下は UrlNode のみだが、
    // 将来的な仕様変更 / 手動 JSON 混入への防御として group を再帰展開して flatten する。
    _extractUrlNodes: function (ruleRoot) {
      if (!ruleRoot || !ruleRoot.conditions || !ruleRoot.conditions.pages) return [];
      var pages = ruleRoot.conditions.pages;
      var out = [];
      var stack = (pages.children || []).slice();
      while (stack.length > 0) {
        var n = stack.shift();
        if (!n) continue;
        if (n.type === 'url') {
          out.push(n);
        } else if (n.type === 'group') {
          stack = (n.children || []).concat(stack);
        }
      }
      return out;
    },
    // PagesGroup.operator を優先し、無ければ RuleRoot.operator にフォールバック。
    // 最終フォールバックは 'or' 固定運用に合わせる (CMS 側の書き込みが常に or のため、operator を持たない
    // レガシー JSON でも「いずれかを満たす」で描画する)。
    _operatorOf: function (ruleRoot) {
      if (ruleRoot && ruleRoot.conditions && ruleRoot.conditions.pages && ruleRoot.conditions.pages.operator) {
        return ruleRoot.conditions.pages.operator;
      }
      return (ruleRoot && ruleRoot.operator) || 'or';
    },
    _operatorLabel: function (op) {
      return op === 'or' ? 'いずれかを満たす' : 'すべてを満たす';
    },
    methodLabel: function (m) {
      var map = { eq: '等しい', notEq: '等しくない', contains: '含む', notContains: '含まない' };
      return map[m] || m;
    },
    hasQuery: function (node) {
      return !!(node && node.querystring && node.querystring.params && node.querystring.params.length > 0);
    },
    // クエリストリング条件を「key=value(methodLabel)」形式で連結する。
    // 複数 params はスラッシュ区切りで一覧化し、空 key/value は無視する(防御的)。
    queryLabel: function (node) {
      var self = this;
      return node.querystring.params
        .filter(function (p) {
          return p && (p.key || p.value);
        })
        .map(function (p) {
          return (p.key || '') + '=' + (p.value || '') + '(' + self.methodLabel(p.method) + ')';
        })
        .join(' / ');
    },
  },
};
</script>

<style scoped>
/* 基本(minimap相当)のコンパクトデザイン。
   一覧・成果一覧の td 幅 248px / padding 15px のセルに最適化した密度。 */
.reminder-cond-summary {
  font-size: 11px;
  line-height: 1.5;
  color: #333;
}
.reminder-cond-summary__sec + .reminder-cond-summary__sec {
  margin-top: 2px;
}
.reminder-cond-summary__h {
  font-weight: 700;
  color: #333;
  font-size: 12px;
}
.reminder-cond-summary__op {
  font-weight: 400;
  color: #666;
  font-size: 10px;
  margin-left: 4px;
}
.reminder-cond-summary__list {
  list-style: none;
  margin: 1px 0 5px;
  padding: 0;
}
.reminder-cond-summary__sec:last-child .reminder-cond-summary__list {
  margin-bottom: 0;
}
.reminder-cond-summary__list li {
  font-size: 11px;
  padding: 0;
  color: #333;
  word-break: break-all;
  overflow-wrap: break-word;
}
.reminder-cond-summary__m {
  color: #666;
  font-size: 10px;
  margin-right: 4px;
}
.reminder-cond-summary__meta {
  color: #888;
  font-size: 10px;
  margin-left: 5px;
  white-space: nowrap;
}
.reminder-cond-summary__trigger {
  margin-top: 4px;
  color: #666;
  font-size: 10px;
}
.reminder-cond-summary__empty {
  color: #999;
  font-size: 11px;
}
/* 各セクションで対象ノードが 1 件も無いときのフォールバック表示 (「(なし)」/ 手動トリガーの発火説明)。
   list の margin-bottom と揃えて、次セクションとの間隔をリストと同じ感覚にする。 */
.reminder-cond-summary__none {
  color: #999;
  font-size: 11px;
  margin: 1px 0 5px;
}
.reminder-cond-summary__sec:last-child .reminder-cond-summary__none {
  margin-bottom: 0;
}

/* detail モード(確認画面・成果詳細カード) はフォントを1段階大きめに。
   密度設計は minimap と共有するためマージンは触らず font-size だけ上げる。 */
.reminder-cond-summary--detail {
  font-size: 12px;
}
.reminder-cond-summary--detail .reminder-cond-summary__h {
  font-size: 13px;
}
.reminder-cond-summary--detail .reminder-cond-summary__op {
  font-size: 11px;
}
.reminder-cond-summary--detail .reminder-cond-summary__list li {
  font-size: 12px;
}
.reminder-cond-summary--detail .reminder-cond-summary__m {
  font-size: 11px;
}
.reminder-cond-summary--detail .reminder-cond-summary__meta {
  font-size: 11px;
}
.reminder-cond-summary--detail .reminder-cond-summary__none {
  font-size: 12px;
}
</style>
